import { formatMoney } from '@theme/utilities';
import { CartUpdatedEvent } from '@theme/events';

class DeployerBundle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-bundle-step]'));
    this.discountPercent = parseFloat(this.dataset.bundleDiscountPercent) || 0;
    this.discountAmount = parseInt(this.dataset.bundleDiscountAmount, 10) || 0;
    this.addToCartContainer = this.querySelector('[data-ref="add-to-cart-button-container"]');
    this.addToCartButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.form = this.querySelector('form');
    this.popups = Array.from(this.querySelectorAll('dialog-component[data-bundle-step]'));
    this.popupTriggers = Array.from(this.querySelectorAll('[data-ref="bundle-popup-trigger"]'));
    this.activePopupTriggers = [];
    this.handlePopupTrigger = this.#handlePopupTrigger.bind(this);
    this.handlePageShow = this.handlePageShow.bind(this);
    this.handleFormSubmit = this.#handleFormSubmit.bind(this);

    this.steps.forEach((step) => this.#initStep(step));
    this.#updateTotals();
    this.#resetButtonState();
    this.popupTriggers.forEach((trigger) => {
      const step = trigger.dataset.popupStep;
      const popup =
        this.popups.find((item) => item.dataset.bundleStep === step) ||
        this.popups.find((item) => item.dataset.bundleStep === 'all');

      if (!popup) {
        trigger.setAttribute('hidden', '');
        trigger.setAttribute('aria-hidden', 'true');
        trigger.setAttribute('tabindex', '-1');
        return;
      }

      this.activePopupTriggers.push(trigger);
      trigger.addEventListener('click', this.handlePopupTrigger);
    });
    if (this.form) {
      this.form.addEventListener('submit', this.handleFormSubmit);
    }
    window.addEventListener('pageshow', this.handlePageShow);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.handlePageShow);
    this.activePopupTriggers?.forEach((trigger) => {
      trigger.removeEventListener('click', this.handlePopupTrigger);
    });
    if (this.form) {
      this.form.removeEventListener('submit', this.handleFormSubmit);
    }
  }

  handlePageShow() {
    this.#resetButtonState();
  }

  #handlePopupTrigger(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.popupStep;
    const popup =
      this.popups.find((item) => item.dataset.bundleStep === step) ||
      this.popups.find((item) => item.dataset.bundleStep === 'all');
    if (!popup) return;
    popup.open();
  }

  #initStep(step) {
    const variantButtons = step.querySelectorAll('[data-bundle-variant]');
    const purchaseInputs = step.querySelectorAll('[data-bundle-purchase]');
    const planInputs = step.querySelectorAll('[data-bundle-plan]');
    const purchaseWrappers = step.querySelectorAll('[data-bundle-option]');

    variantButtons.forEach((button) => {
      if (button.tagName === 'INPUT') {
        button.addEventListener('change', () => this.#onVariantClick(step, button));
      } else {
        button.addEventListener('click', () => this.#onVariantClick(step, button));
      }
    });

    purchaseInputs.forEach((input) => {
      input.addEventListener('change', () => this.#onPurchaseChange(step, input));
    });

    planInputs.forEach((input) => {
      input.addEventListener('change', () => this.#onPlanChange(step, input));
    });

    purchaseWrappers.forEach((wrapper) => {
      wrapper.addEventListener('pointerup', (event) => this.#onPurchasePointerUp(step, event));
    });

    const selectedVariant = this.#getSelectedVariant(step) || variantButtons[0];
    if (selectedVariant) {
      this.#applyVariant(step, selectedVariant);
    }

    const selectedPurchase = step.querySelector('[data-bundle-purchase]:checked');
    if (selectedPurchase) {
      this.#applyPurchase(step, selectedPurchase);
    }

    this.#syncPlanSelection(step);
    this.#syncSubscriptionSelection(step);
    this.#updateSubscriptionHeaderPrice(step);
  }

  #onVariantClick(step, button) {
    if (button.hasAttribute('disabled')) return;
    this.#applyVariant(step, button);
    this.#updateTotals();
  }

  #onPurchaseChange(step, input) {
    this.#applyPurchase(step, input);
    this.#updateTotals();
  }

  #onPurchasePointerUp(step, event) {
    if (event.pointerType && event.pointerType !== 'touch') return;

    const option = event.target.closest('[data-bundle-option]');
    if (!option) return;

    const input = option.querySelector('[data-bundle-purchase]');
    if (!input || input.checked) return;

    input.checked = true;
    this.#onPurchaseChange(step, input);
  }

  #onPlanChange(step) {
    this.#syncSellingPlanInput(step);
    this.#syncPlanSelection(step);
    this.#updateSubscriptionHeaderPrice(step);
    this.#updateTotals();
  }

  #applyVariant(step, button) {
    step.querySelectorAll('[data-bundle-variant]').forEach((item) => item.classList.remove('is-selected'));
    if (button.tagName !== 'INPUT') {
      button.classList.add('is-selected');
    }

    const variantIdInput = step.querySelector('[data-ref="bundle-variant-id"]');
    const image = step.querySelector('[data-ref="bundle-step-image"]');

    if (variantIdInput) {
      variantIdInput.value = button.dataset.variantId;
    }

    if (image && button.dataset.variantImage) {
      image.src = button.dataset.variantImage;
      image.alt = button.dataset.variantImageAlt || image.alt || '';
    }

    this.#updatePlanPrices(step, button);
    this.#updateOptionPrices(step, button);
    this.#syncSellingPlanInput(step);
  }

  #applyPurchase(step, input) {
    step.dataset.selectedPurchase = input.value;
    this.#syncSellingPlanInput(step);
    this.#syncSubscriptionSelection(step);
  }

  #syncSellingPlanInput(step) {
    const sellingPlanInput = step.querySelector('[data-ref="bundle-selling-plan"]');
    const selectedPurchase = step.dataset.selectedPurchase;
    let selectedPlanInput = step.querySelector('[data-bundle-plan]:checked');

    if (!sellingPlanInput) return;

    if (selectedPurchase === 'subscription' && !selectedPlanInput) {
      const firstPlanInput = step.querySelector('[data-bundle-plan]');
      if (firstPlanInput) {
        firstPlanInput.checked = true;
        selectedPlanInput = firstPlanInput;
      }
    }

    if (selectedPurchase === 'subscription' && selectedPlanInput?.value) {
      sellingPlanInput.disabled = false;
      sellingPlanInput.value = selectedPlanInput.value;
    } else {
      sellingPlanInput.value = '';
      sellingPlanInput.disabled = true;
    }
  }

  #syncPlanSelection(step) {
    const planItems = step.querySelectorAll('[data-bundle-plan-item]');
    planItems.forEach((item) => item.classList.remove('is-selected'));

    const selectedPlanInput = step.querySelector('[data-bundle-plan]:checked');
    const selectedPlanItem = selectedPlanInput?.closest('[data-bundle-plan-item]');
    if (selectedPlanItem) {
      selectedPlanItem.classList.add('is-selected');
    }
  }

  #getSelectedVariant(step) {
    return (
      step.querySelector('[data-bundle-variant]:checked') ||
      step.querySelector('[data-bundle-variant].is-selected')
    );
  }

  #resetButtonState() {
    if (!this.addToCartButton) return;
    this.addToCartButton.classList.remove('is-loading');
    this.addToCartButton.disabled = false;
  }

  #syncSubscriptionSelection(step) {
    const selectedPurchase = step.dataset.selectedPurchase;
    const options = step.querySelectorAll('[data-bundle-option]');

    options.forEach((option) => {
      const optionType = option.dataset.bundleOption;
      if (optionType === selectedPurchase) {
        option.classList.add('subscription__option--selected');
        option.classList.remove('subscription-widget__option--selected');
      } else {
        option.classList.remove('subscription__option--selected');
        option.classList.remove('subscription-widget__option--selected');
      }
    });
  }

  #updateOptionPrices(step, variantButton) {
    const basePrice = parseInt(variantButton.dataset.variantPrice, 10) || 0;
    const compareAtPrice = parseInt(variantButton.dataset.variantCompareAtPrice, 10) || 0;

    const onetimeContainer = step.querySelector('[data-ref="one-time-price"]');
    this.#updatePriceElement(onetimeContainer, basePrice, compareAtPrice, false);

    this.#updateSubscriptionHeaderPrice(step);
  }

  #updateSubscriptionHeaderPrice(step) {
    const subscriptionContainer = step.querySelector('[data-ref="subscription-price"]');
    if (!subscriptionContainer) return;

    const selectedVariant = this.#getSelectedVariant(step);
    const basePrice = selectedVariant ? parseInt(selectedVariant.dataset.variantPrice, 10) || 0 : 0;

    const selectedPlan = step.querySelector('[data-bundle-plan]:checked');
    let planPrice = selectedPlan ? parseInt(selectedPlan.dataset.planPrice, 10) : null;
    if (!Number.isFinite(planPrice) || planPrice <= 0) {
      planPrice = basePrice;
    }

    if (planPrice !== null) {
      this.#updatePriceElement(subscriptionContainer, planPrice, basePrice, true);
    }
  }

  #updatePlanPrices(step, variantButton) {
    const planInputs = step.querySelectorAll('[data-bundle-plan]');
    if (!planInputs.length) return;

    let sellingPlans = [];
    try {
      sellingPlans = JSON.parse(variantButton.dataset.variantSellingPlans || '[]');
    } catch (error) {
      sellingPlans = [];
    }

    planInputs.forEach((planInput) => {
      const allocation = sellingPlans.find((item) => `${item.selling_plan?.id}` === planInput.value);
      const planPrice = allocation?.price ?? parseInt(variantButton.dataset.variantPrice, 10);
      planInput.dataset.planPrice = planPrice;

      const priceLabel = planInput.closest('[data-bundle-plan-item]')?.querySelector('[data-ref="bundle-plan-price"]');
      if (priceLabel) {
        priceLabel.textContent = formatMoney(planPrice);
      }
    });
  }

  #updatePriceElement(container, price, compareAtPrice, showSubscriptionBadge = false) {
    if (!container) return;

    const priceEl = container.querySelector('[data-ref="price"]');
    const compareAtPriceEl = container.querySelector('[data-ref="compare-at-price"]');
    const badgeEl = container.querySelector('[data-ref="difference-price-value"]');

    if (priceEl) {
      priceEl.textContent = formatMoney(price);
      priceEl.dataset.value = price;
    }

    const hasSavings = compareAtPrice > price;

    if (compareAtPriceEl) {
      if (hasSavings) {
        compareAtPriceEl.textContent = formatMoney(compareAtPrice);
        compareAtPriceEl.dataset.value = compareAtPrice;
      } else {
        compareAtPriceEl.textContent = '';
        compareAtPriceEl.dataset.value = '';
      }
    }

    if (badgeEl) {
      badgeEl.textContent = showSubscriptionBadge && hasSavings ? formatMoney(compareAtPrice - price) : '';
      badgeEl.dataset.showMinus = showSubscriptionBadge ? 'true' : 'false';
    }
  }

  #getStepPrice(step) {
    const selectedVariant = this.#getSelectedVariant(step);
    if (!selectedVariant) return { base: 0, selected: 0 };

    const basePrice = parseInt(selectedVariant.dataset.variantPrice, 10) || 0;
    const selectedPurchase = step.dataset.selectedPurchase;

    if (selectedPurchase !== 'subscription') {
      return { base: basePrice, selected: basePrice };
    }

    const selectedPlanInput = step.querySelector('[data-bundle-plan]:checked');
    const planPrice = selectedPlanInput ? parseInt(selectedPlanInput.dataset.planPrice, 10) : basePrice;

    return { base: basePrice, selected: planPrice };
  }

  #getDiscountValue(totalPrice) {
    if (this.discountAmount > 0) return this.discountAmount;
    if (this.discountPercent > 0) return Math.round((totalPrice * this.discountPercent) / 100);
    return 0;
  }

  #updateTotals() {
    if (!this.addToCartContainer) return;

    let baseTotal = 0;
    let selectedTotal = 0;

    this.steps.forEach((step) => {
      const stepPrices = this.#getStepPrice(step);
      baseTotal += stepPrices.base;
      selectedTotal += stepPrices.selected;
    });

    const discountValue = this.#getDiscountValue(selectedTotal);
    const discountedTotal = Math.max(selectedTotal - discountValue, 0);

    this.addToCartContainer.dataset.price = discountedTotal;

    if (discountValue > 0 && selectedTotal > discountedTotal) {
      this.addToCartContainer.dataset.compareAtPrice = selectedTotal;
    } else {
      delete this.addToCartContainer.dataset.compareAtPrice;
    }

    const priceElements = this.addToCartContainer.querySelectorAll('[data-ref="price"]');
    const compareAtPriceElements = this.addToCartContainer.querySelectorAll('[data-ref="compare-at-price"]');

    priceElements.forEach((priceElement) => {
      priceElement.dataset.value = discountedTotal;
      priceElement.textContent = formatMoney(discountedTotal);
    });

    compareAtPriceElements.forEach((compareAtPriceElement) => {
      if (discountValue > 0 && selectedTotal > discountedTotal) {
        compareAtPriceElement.dataset.value = selectedTotal;
        compareAtPriceElement.textContent = formatMoney(selectedTotal);
      } else {
        compareAtPriceElement.dataset.value = '';
        compareAtPriceElement.textContent = '';
      }
    });
  }

  #handleFormSubmit(event) {
    event.preventDefault();
    if (!this.addToCartButton) return;

    this.addToCartButton.classList.add('is-loading');
    this.addToCartButton.disabled = true;

    const items = [];
    const itemsData = {};

    // Collecter les items au format items[0][id], items[0][selling_plan]
    this.form.querySelectorAll('input[name^="items["], select[name^="items["]').forEach((input) => {
      const name = input.name;
      const indexMatch = name.match(/items\[(\d+)\]/);
      if (!indexMatch) return;

      const index = indexMatch[1];

      if (!itemsData[index]) {
        itemsData[index] = {};
      }

      if (name.includes('[id]')) {
        itemsData[index].id = input.value;
      } else if (name.includes('[quantity]')) {
        itemsData[index].quantity = parseInt(input.value, 10) || 1;
      } else if (name.includes('[selling_plan]')) {
        if (input.value && !input.disabled) {
          itemsData[index].selling_plan = input.value;
        }
      }
    });

    // Convertir l'objet en array
    for (const key in itemsData) {
      if (itemsData[key].id) {
        items.push(itemsData[key]);
      }
    }

    if (items.length === 0) {
      console.error('No items to add to cart');
      this.addToCartButton.classList.remove('is-loading');
      this.addToCartButton.disabled = false;
      return;
    }

    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      sections.push(cartDrawer.dataset.sectionId);
    }

    const quantity = items.reduce((acc, item) => acc + (item.quantity || 1), 0);

    const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: items,
        sections: sections.join(','),
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((response) => {
        const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();
        let newItemCount;
        if (currentItemCount != null) {
          newItemCount = currentItemCount + quantity;
        }

        // Déclencher l'événement de mise à jour du panier
        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: newItemCount,
              source: 'deployer-bundle',
              sections: response.sections || {},
            },
          ),
        );

        this.addToCartButton.classList.remove('is-loading');
        this.addToCartButton.disabled = false;
      })
      .catch((error) => {
        console.error('Error adding bundle to cart:', error);
        this.addToCartButton.classList.remove('is-loading');
        this.addToCartButton.disabled = false;
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: { type: 'error', message: "Erreur lors de l'ajout au panier" },
          }),
        );
      });
  }
}

if (!customElements.get('deployer-bundle')) {
  customElements.define('deployer-bundle', DeployerBundle);
}

